
'use client';

import { useAuth } from '@/hooks/useAuth';
import { Loader2, TrendingUp, PiggyBank, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import {
  JartidexHistory, FixedTermDeposit, JartidexInvestment, BankAccount, BankSettings,
} from '@/lib/types';
import {
  getJartidexHistory, getBankSettings, createFixedTermDeposit, createJartidexInvestment,
  sellJartidexInvestment, getMyInvestments,
} from '@/lib/actions';
import { format, isPast } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

// ─── Jartidex chart ───────────────────────────────────────────────────────────

const JartidexChart = ({ data }: { data: JartidexHistory[] }) => {
  const formatted = useMemo(() => data.map(d => ({
    date: format(new Date(d.date), 'dd/MM'),
    value: d.value,
    change: d.changePercent,
  })), [data]);

  const min = Math.min(...formatted.map(d => d.value));
  const max = Math.max(...formatted.map(d => d.value));
  const isUp = formatted.length > 1 && formatted[formatted.length - 1].value >= formatted[0].value;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={formatted} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="jdxGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={isUp ? 'hsl(var(--primary))' : 'hsl(var(--destructive))'} stopOpacity={0.4} />
            <stop offset="95%" stopColor={isUp ? 'hsl(var(--primary))' : 'hsl(var(--destructive))'} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border)/0.35)" />
        <XAxis dataKey="date" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} axisLine={false} tickLine={false} domain={[min * 0.98, max * 1.02]} tickFormatter={v => v.toFixed(0)} />
        <Tooltip
          contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, fontSize: 12 }}
          formatter={(v: number) => [`${v.toFixed(2)} J/part.`, 'Valor']}
        />
        <Area type="monotone" dataKey="value" stroke={isUp ? 'hsl(var(--primary))' : 'hsl(var(--destructive))'} strokeWidth={2} fill="url(#jdxGrad)" dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// ─── Amount selector ──────────────────────────────────────────────────────────

const AmountSelector = ({
  value,
  onChange,
  max,
  label,
  unit = 'Jartis',
}: {
  value: string;
  onChange: (v: string) => void;
  max: number;
  label: string;
  unit?: string;
}) => {
  const pcts = [25, 50, 75, 100];
  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      <div className="relative">
        <Input
          type="number"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="h-12 pr-16 text-lg font-semibold"
          placeholder="0.00"
          min={0}
          step={0.01}
        />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">{unit}</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {pcts.map(p => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(((max * p) / 100).toFixed(2))}
            className="h-8 rounded-lg text-xs font-semibold bg-white/5 border border-white/10 hover:bg-primary/20 hover:border-primary/40 hover:text-primary transition-colors"
          >
            {p}%
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Disponible: {max.toLocaleString('es-ES', { minimumFractionDigits: 2 })} {unit}</p>
    </div>
  );
};

// ─── Deposit dialog ───────────────────────────────────────────────────────────

const DepositDialog = ({
  open,
  onClose,
  rate,
  account,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  rate: { days: number; rate: number };
  account: BankAccount;
  onDone: () => void;
}) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const numAmt = parseFloat(amount) || 0;
  const payout = numAmt * (1 + (rate.rate * rate.days / 365));
  const profit = payout - numAmt;

  const submit = async () => {
    if (numAmt <= 0) { toast({ title: 'Cantidad no válida', variant: 'destructive' }); return; }
    if (numAmt > account.oro) { toast({ title: 'Saldo insuficiente', variant: 'destructive' }); return; }
    setLoading(true);
    const res = await createFixedTermDeposit(account.id, numAmt, rate.days);
    if (res.success) {
      toast({ title: '¡Depósito creado!', description: res.message });
      onDone(); setAmount(''); onClose();
    } else {
      toast({ title: 'Error', description: res.message, variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Depósito a {rate.days} días — {(rate.rate * 100).toFixed(2)}% TAE</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-3">
          <AmountSelector value={amount} onChange={setAmount} max={account.oro} label="Cantidad a depositar" />
          {numAmt > 0 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-primary/6 border border-primary/20 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Depositas</span>
                <span className="font-semibold">{numAmt.toFixed(2)} J</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Interés ganado</span>
                <span className="font-semibold text-green-400">+{profit.toFixed(2)} J</span>
              </div>
              <div className="flex justify-between text-sm border-t border-white/10 pt-2">
                <span className="font-bold">Recibirás al vencer</span>
                <span className="font-black text-primary">{payout.toFixed(2)} J</span>
              </div>
              <p className="text-xs text-muted-foreground">Vencimiento en {rate.days} días</p>
            </motion.div>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
          <Button onClick={submit} disabled={loading || numAmt <= 0}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Active deposits list ─────────────────────────────────────────────────────

const ActiveDeposits = ({ deposits }: { deposits: FixedTermDeposit[] }) => {
  const active = useMemo(() => [...deposits].filter(d => d.status === 'active').sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime()), [deposits]);
  if (!active.length) return <p className="text-sm text-muted-foreground text-center py-6">No tienes depósitos activos.</p>;
  return (
    <div className="space-y-2.5">
      {active.map(d => {
        const end = new Date(d.endDate);
        const done = isPast(end);
        return (
          <div key={d.id} className="flex items-center gap-4 p-4 rounded-2xl bg-white/4 border border-white/8">
            <div className="p-2 rounded-xl bg-primary/10 shrink-0"><PiggyBank className="h-5 w-5 text-primary" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{d.amount.toFixed(2)} J</span>
                <Badge variant="secondary" className="text-xs">{d.termDays} días</Badge>
                <Badge className={cn('text-xs', done ? 'bg-green-500/20 text-green-400' : 'bg-primary/20 text-primary')}>{done ? 'Vencido' : 'Activo'}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {done ? 'Venció el' : 'Vence el'} {format(end, 'dd/MM/yyyy')} · Recibirás {d.payoutAmount.toFixed(2)} J
              </p>
            </div>
            <span className="text-green-400 font-bold text-sm shrink-0">+{(d.payoutAmount - d.amount).toFixed(2)} J</span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Portfolio summary ────────────────────────────────────────────────────────

const PortfolioSummary = ({
  deposits,
  investments,
  currentJdxValue,
}: {
  deposits: FixedTermDeposit[];
  investments: JartidexInvestment[];
  currentJdxValue: number;
}) => {
  const activeDeposits = deposits.filter(d => d.status === 'active');
  const activeInvestments = investments.filter(i => i.status === 'active');

  const totalDeposited = activeDeposits.reduce((s, d) => s + d.amount, 0);
  const totalJdx = activeInvestments.reduce((s, i) => s + i.shares, 0) * currentJdxValue;
  const total = totalDeposited + totalJdx;

  if (total === 0) return null;

  return (
    <div className="p-5 rounded-3xl bg-card/50 backdrop-blur border border-white/8 space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Cartera total</p>
          <p className="text-3xl font-black mt-1">{total.toLocaleString('es-ES', { minimumFractionDigits: 2 })} <span className="text-primary text-xl">J</span></p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        {[
          { label: 'Depósitos', value: totalDeposited, color: 'text-blue-400' },
          { label: 'Jartidex35', value: totalJdx, color: 'text-primary' },
        ].map(({ label, value, color }) => (
          <div key={label} className="p-3 rounded-2xl bg-white/5 border border-white/8">
            <p className="text-xs text-muted-foreground mb-1">{label}</p>
            <p className={cn('font-bold', color)}>{value.toLocaleString('es-ES', { minimumFractionDigits: 2 })} J</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Product section wrapper ──────────────────────────────────────────────────

const ProductSection = ({
  id,
  icon: Icon,
  title,
  subtitle,
  badge,
  badgeColor,
  children,
}: {
  id: string;
  icon: React.ElementType;
  title: string;
  subtitle: string;
  badge?: string;
  badgeColor?: string;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-3xl bg-card/50 backdrop-blur border border-white/8 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center gap-4 p-5 hover:bg-white/4 transition-colors"
        onClick={() => setOpen(v => !v)}
      >
        <div className="p-3 rounded-2xl bg-primary/10 shrink-0"><Icon className="h-5 w-5 text-primary" /></div>
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold">{title}</span>
            {badge && <span className={cn('text-xs px-2 py-0.5 rounded-full font-semibold', badgeColor || 'bg-primary/20 text-primary')}>{badge}</span>}
          </div>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
            <div className="px-5 pb-5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Jartidex interaction ─────────────────────────────────────────────────────

const JartidexPanel = ({
  account,
  currentValue,
  investments,
  onDone,
}: {
  account: BankAccount;
  currentValue: number;
  investments: JartidexInvestment[];
  onDone: () => void;
}) => {
  const [tab, setTab] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const totalShares = useMemo(() => investments.filter(i => i.status === 'active').reduce((s, i) => s + i.shares, 0), [investments]);
  const numAmt = parseFloat(amount) || 0;

  const execute = async () => {
    if (numAmt <= 0) return;
    setLoading(true);
    const res = tab === 'buy'
      ? await createJartidexInvestment(account.id, numAmt)
      : await sellJartidexInvestment(account.id, numAmt);
    if (res.success) { toast({ title: 'Operación realizada', description: res.message }); onDone(); setAmount(''); }
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="inline-flex rounded-xl bg-white/5 border border-white/10 p-1">
        {(['buy', 'sell'] as const).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => { setTab(t); setAmount(''); }}
            className={cn('px-5 py-1.5 rounded-lg text-sm font-semibold transition-all', tab === t ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground')}
          >
            {t === 'buy' ? 'Comprar' : 'Vender'}
          </button>
        ))}
      </div>

      {tab === 'buy'
        ? <AmountSelector value={amount} onChange={setAmount} max={account.oro} label="Cantidad a invertir (Jartis)" />
        : (
          <div className="space-y-3">
            <AmountSelector value={amount} onChange={setAmount} max={parseFloat(totalShares.toFixed(4))} label="Participaciones a vender" unit="part." />
            <p className="text-xs text-muted-foreground">Tienes {totalShares.toFixed(6)} participaciones</p>
          </div>
        )}

      {numAmt > 0 && currentValue > 0 && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="p-3 rounded-xl bg-primary/6 border border-primary/20 text-sm">
          {tab === 'buy'
            ? <p>Recibirás aprox. <strong className="text-primary">{(numAmt / currentValue).toFixed(6)} participaciones</strong></p>
            : <p>Recibirás aprox. <strong className="text-primary">{(numAmt * currentValue).toFixed(2)} J</strong></p>}
          <p className="text-xs text-muted-foreground mt-0.5">Precio actual: {currentValue.toFixed(2)} J/part.</p>
        </motion.div>
      )}

      <Button onClick={execute} disabled={loading || numAmt <= 0} className="w-full">
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {tab === 'buy' ? 'Comprar participaciones' : 'Vender participaciones'}
      </Button>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export default function JartinetInversionesPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const account = location.state?.account as BankAccount;

  const [history, setHistory] = useState<JartidexHistory[]>([]);
  const [settings, setSettings] = useState<BankSettings | null>(null);
  const [deposits, setDeposits] = useState<FixedTermDeposit[]>([]);
  const [investments, setInvestments] = useState<JartidexInvestment[]>([]);
  const [pageLoading, setPageLoading] = useState(true);

  // Deposit dialog state
  const [depositRate, setDepositRate] = useState<{ days: number; rate: number } | null>(null);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!user || !account?.id) { setPageLoading(false); return; }
    try {
      const [histRes, settRes, invRes] = await Promise.all([
        getJartidexHistory(),
        getBankSettings(),
        getMyInvestments(account.id),
      ]);
      if (histRes.history) setHistory(histRes.history);
      if (settRes.settings) setSettings(settRes.settings);
      if (invRes.success) { setDeposits(invRes.deposits); setInvestments(invRes.investments); }
    } catch (e) {
      console.error('Error loading investment data', e);
    } finally {
      setPageLoading(false);
    }
  }, [user, account?.id]);

  useEffect(() => {
    if (!loading) {
      if (!user || !account) navigate('/jartinet/banco', { replace: true });
      else fetchAll();
    }
  }, [user, loading, navigate, account, fetchAll]);

  if (loading || pageLoading) {
    return (
      <div className="flex h-60 items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Cargando inversiones...</p>
      </div>
    );
  }

  const currentValue = history.length ? history[history.length - 1].value : 0;
  const prevValue = history.length > 1 ? history[history.length - 2].value : currentValue;
  const jdxChange = prevValue ? ((currentValue - prevValue) / prevValue) * 100 : 0;
  const jdxUp = jdxChange >= 0;

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2">
        <ArrowLeft className="mr-2 h-4 w-4" /> Volver a mi cuenta
      </Button>

      <div>
        <h1 className="text-3xl font-black">Inversiones</h1>
        <p className="text-muted-foreground mt-1">Cuenta: <span className="font-semibold text-foreground">{account.accountName}</span> · {account.oro.toLocaleString('es-ES', { minimumFractionDigits: 2 })} J disponibles</p>
      </div>

      {/* Portfolio summary */}
      <PortfolioSummary deposits={deposits} investments={investments} currentJdxValue={currentValue} />

      {/* ── Jartidex 35 ── */}
      <ProductSection
        id="jartidex"
        icon={TrendingUp}
        title="Jartidex35"
        subtitle="Índice bursátil de la comunidad cofrade · Variable"
        badge={`${jdxChange >= 0 ? '+' : ''}${jdxChange.toFixed(2)}% hoy`}
        badgeColor={jdxUp ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}
      >
        <div className="space-y-5">
          {/* Price + chart */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Precio actual</p>
              <p className="text-2xl font-black">{currentValue.toFixed(2)} <span className="text-primary">J/part.</span></p>
            </div>
            <Badge className={cn('text-sm px-3 py-1', jdxUp ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400')}>
              {jdxUp ? '▲' : '▼'} {Math.abs(jdxChange).toFixed(2)}%
            </Badge>
          </div>
          {history.length > 0 && <JartidexChart data={history} />}

          <JartidexPanel account={account} currentValue={currentValue} investments={investments} onDone={fetchAll} />

          {/* Active positions */}
          {investments.filter(i => i.status === 'active').length > 0 && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Mis participaciones</p>
              {investments.filter(i => i.status === 'active').map(inv => {
                const gain = (inv.shares * currentValue) - inv.totalCost;
                const gainPct = inv.totalCost > 0 ? (gain / inv.totalCost) * 100 : 0;
                return (
                  <div key={inv.id} className="flex items-center justify-between p-3 rounded-xl bg-white/4 border border-white/8 text-sm">
                    <div>
                      <p className="font-semibold">{inv.shares.toFixed(6)} part.</p>
                      <p className="text-xs text-muted-foreground">Comprado a {inv.purchasePrice.toFixed(2)} J</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{(inv.shares * currentValue).toFixed(2)} J</p>
                      <p className={cn('text-xs', gain >= 0 ? 'text-green-400' : 'text-red-400')}>
                        {gain >= 0 ? '+' : ''}{gain.toFixed(2)} J ({gainPct.toFixed(2)}%)
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </ProductSection>

      {/* ── Depósitos ── */}
      <ProductSection
        id="deposits"
        icon={PiggyBank}
        title="Depósitos a Plazo Fijo"
        subtitle="Rentabilidad garantizada · Bloqueo temporal"
        badge="Garantizado"
        badgeColor="bg-blue-500/20 text-blue-400"
      >
        <div className="space-y-5">
          {settings?.fixedDepositRates && settings.fixedDepositRates.length > 0
            ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {settings.fixedDepositRates.map((r, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setDepositRate(r); setDepositDialogOpen(true); }}
                    className="group p-5 rounded-2xl bg-white/4 border border-white/8 hover:border-primary/40 hover:bg-primary/6 transition-all text-left"
                  >
                    <p className="text-2xl font-black text-primary">{r.days}<span className="text-base font-semibold"> días</span></p>
                    <p className="text-base font-bold text-green-400 mt-1">{(r.rate * 100).toFixed(2)}% TAE</p>
                    <p className="text-xs text-muted-foreground mt-1">Rentabilidad garantizada</p>
                    <div className="mt-3 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">Contratar →</div>
                  </button>
                ))}
              </div>
            )
            : <p className="text-sm text-muted-foreground">Sin productos disponibles.</p>}

          {/* Active deposits */}
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Mis depósitos</p>
            <ActiveDeposits deposits={deposits} />
          </div>
        </div>
      </ProductSection>

      {/* Dialogs */}
      {depositRate && (
        <DepositDialog
          open={depositDialogOpen}
          onClose={() => { setDepositDialogOpen(false); setDepositRate(null); }}
          rate={depositRate}
          account={account}
          onDone={fetchAll}
        />
      )}
    </div>
  );
}
